import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, Component, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { EnvironmentsService, IOcbEnvironmentLog, OcbEnvironmentLogTypes, UserService, IOcbFileTransferLog, IOcbFileTransfer, FileTransfersService, OcbFileTransferLogTypes, IOcbRemotePlatform, IOcbRemotePlatformLog, RemotePlatformsService, OcbRemotePlatformLogTypes } from '@app/core';
import { TranslateService } from '@ngx-translate/core';
import { DxDataGridComponent } from 'devextreme-angular/ui/data-grid';
import notify from 'devextreme/ui/notify';
import { Observable, forkJoin } from 'rxjs';
import { finalize, mergeMap, tap } from 'rxjs/operators';
import { DxTabPanelComponent } from 'devextreme-angular/ui/tab-panel';
import { EnvironmentLogComponent, FileTransferLogComponent, FileTransferPickerComponent, RemotePlatformLogComponent, RemotePlatformPickerComponent } from '../../shared';

const now = new Date();
const initialDates: [Date, Date] = [new Date(now), new Date(now)];

@Component({
    selector: 'app-logs',
    templateUrl: './logs.component.html',
    styleUrls: ['./logs.component.scss'],
})
export class LogsComponent implements AfterViewInit {

    // Tabs
    @ViewChild('tabs')
    public tabs: DxTabPanelComponent;

    // Date selectors
    public maxDate: Date = now;
    public selectedDates: [Date, Date] = initialDates;
    // Internal identifier of the current environment
    private currentEnvironmentId: string = '';
    // Name of the current environment
    public currentEnvironmentName: string = '';
    // Boolean to show or hide the loading panel
    public isLoadPanelVisible: boolean = true;

    public showFileTransferColumn: boolean = false;
    public showRemotePlatformColumn: boolean = false;

    constructor(
        private route: ActivatedRoute,
        private userService: UserService,
        private environmentsService: EnvironmentsService,
        private fileTransfersService: FileTransfersService,
        private remotePlatformsService: RemotePlatformsService,
        private translateService: TranslateService) {
        this.displayFileTransferLogDate = this.displayFileTransferLogDate.bind(this);
        this.displayFileTransferLogDateAsString = this.displayFileTransferLogDateAsString.bind(this);
        this.displayFileTransferLogType = this.displayFileTransferLogType.bind(this);
        this.displayRemotePlatformLogDate = this.displayRemotePlatformLogDate.bind(this);
        this.displayRemotePlatformLogDateAsString = this.displayRemotePlatformLogDateAsString.bind(this);
        this.displayRemotePlatformLogType = this.displayRemotePlatformLogType.bind(this);
        this.displayEnvironmentLogDate = this.displayEnvironmentLogDate.bind(this);
        this.displayEnvironmentLogDateAsString = this.displayEnvironmentLogDateAsString.bind(this);
        this.displayEnvironmentLogType = this.displayEnvironmentLogType.bind(this);
    }

    public ngAfterViewInit() {
        forkJoin([
            this.translateService.get('environmentLogTypes'),
            this.translateService.get('fileTransfersLogTypes'),
            this.translateService.get('remotePlatformLogTypes')
        ])
            .subscribe(messages => {
                this.environmentLogsTypesLabels = messages[0];
                this.fileTransfersLogsTypesLabels = messages[1];
                this.remotePlatformsLogsTypesLabels = messages[2];
            });
        this.route.queryParams.subscribe(queryParams => {
            if (queryParams['fileTransfer']) this.requestedFileTransferId = queryParams['fileTransfer'];
            else if (queryParams['remotePlatform']) this.requestedPlatformId = queryParams['remotePlatform'];
        });
        this.route.params.subscribe(_ => {
            this.currentEnvironmentId = this.route.snapshot.params.id;
            this.environmentsService.getEnvironment(this.currentEnvironmentId).subscribe(env => {
                this.currentEnvironmentName = env.name;
            });
            this.loadFileTransfers();
            this.loadRemotePlatforms();
        });
    }

    public dateChanged(): void {
        if (this.tabs.selectedIndex === 0) {
            this.loadFileTransferLogs();
        }
        else if (this.tabs.selectedIndex === 1) {
            this.loadRemotePlatformLogs();
        }
        else if (this.tabs.selectedIndex === 2) {
            this.loadEnvironmentLogs();
        }
    }

    public displayDate(dateValue): Date {
        const date = new Date(dateValue + 'Z');
        return date;
    }

    private getCurrentLocale(): string {
        let currentLocale = 'en-US';
        switch (this.userService.language) {
            case 'en': currentLocale = 'en-US'; break;
            case 'fr': currentLocale = 'fr-FR'; break;
        }
        return currentLocale;
    }

    private getSelectedEndDate() { return this.selectedDates[1].toJSON().slice(0, 10); }

    private getSelectedStartDate() { return this.selectedDates[0].toJSON().slice(0, 10); }

    private handleError(error: any): void {
        let message: '';
        if (error instanceof HttpErrorResponse) {
            if (error.error.hasOwnProperty('message')) {
                message = error.error.message;
            } else if (error.error.hasOwnProperty('name')) {
                message = error.error.name;
            } else {
                message = error.error;
            }
        } else {
            message = error;
        }

        notify(message, 'error', 3000);
    }


    //#region File transfers logs

    // Data Grid
    @ViewChild('fileTransferLogsGrid')
    public fileTransferLogsGrid: DxDataGridComponent;
    @ViewChild('fileTransferLogPopup')
    fileTransferLogPopup: FileTransferLogComponent;
    @ViewChild('fileTransferPicker')
    public fileTransferPicker: FileTransferPickerComponent;
    // File transfer logs
    public fileTransfers: IOcbFileTransfer[] = null;
    public fileTransferLogs: IOcbFileTransferLog[] = null;
    private requestedFileTransferId?: string;
    public selectedFileTransfer: IOcbFileTransfer = null;
    // Popup Display
    public displayedFileTransferLog: IOcbFileTransferLog = null;
    // Logs types labels in JSON object
    private fileTransfersLogsTypesLabels: any[];


    public displayFileTransferLogDate(rowData): Date {
        return this.displayDate(rowData.transferDate);
    }

    public displayFileTransferLogDateAsString(rowData): string {
        const locale = this.getCurrentLocale();
        return this.displayDate(rowData.transferDate).toLocaleString(locale);
    }

    public displayFileTransferLogType(rowData) {
        return this.fileTransfersLogsTypesLabels[OcbFileTransferLogTypes[rowData.logType]];
    }

    public fileTransferChanged(data): void {
        if (data) {
            this.selectedFileTransfer = data;
            this.loadFileTransferLogs();
        } else {
            this.fileTransferLogs = [];
        }
    }


    public formatArchivedFileSize(rowData): string {
        if (rowData.archivedFileSize == null) return null;
        return !rowData.archivedFileSize ? '0.00' : rowData.archivedFileSize.toFixed(2);
    }

    public formatSourceFileSize(rowData): string {
        if (rowData.sourceFileSize == null) return null;
        return !rowData.sourceFileSize ? '0.00' : rowData.sourceFileSize.toFixed(2);
    }

    public formatTargetFileSize(rowData): string {
        if (rowData.targetFileSize == null) return null;
        return !rowData.targetFileSize ? '0.00' : rowData.targetFileSize.toFixed(2);
    }

    private loadFileTransfers(): void {
        this.currentEnvironmentId = this.route.snapshot.params.id;
        this.fileTransfersService.getFileTransfers(this.currentEnvironmentId, true)
            .pipe(finalize(() => this.isLoadPanelVisible = false))
            .subscribe({
                next: (res) => {
                    this.translateService.get('_commons.all').subscribe((msg) => {
                        const allTransfers = { id: 'all', name: msg } as IOcbFileTransfer;
                        res.unshift(allTransfers);
                        this.fileTransfers = res;
                        if (this.requestedFileTransferId) this.fileTransferPicker.selectFileTransfer(this.requestedFileTransferId);
                    });
                },
                error: (err) => this.handleError(err)
            });
    }

    private loadFileTransferLogs(): void {
        if (this.selectedFileTransfer) {
            this.showFileTransferLoading$('_commons.loading')
                .pipe(
                    mergeMap(() => {
                        if (this.selectedFileTransfer.id !== 'all') {
                            this.showFileTransferColumn = false;
                            return this.fileTransfersService.getLogs(this.selectedFileTransfer.id, this.getSelectedStartDate(), this.getSelectedEndDate())
                        }
                        else {
                            this.showFileTransferColumn = true;
                            return this.fileTransfersService.getAllLogs(this.getSelectedStartDate(), this.getSelectedEndDate(), this.currentEnvironmentId)
                        }
                    }),
                    finalize(() => this.fileTransferLogsGrid.instance.endCustomLoading()))
                .subscribe({
                    next: (res) => this.fileTransferLogs = res,
                    error: (err) => this.handleError(err)
                });
        }
    }


    public onFileTransferRowDblClick(e) {
        if (e?.data) {
            this.showFileTransferDetail(e, e.data);
        }
    }

    public showFileTransferDetail(e, data) {
        this.displayedFileTransferLog = data;
        this.fileTransferLogPopup.visible = true;
    }

    private showFileTransferLoading$(messageKey: string): Observable<void> {
        return this.translateService.get(messageKey).pipe(
            tap(message => this.fileTransferLogsGrid.instance.beginCustomLoading(`${message}...`)),
        );
    }

    //#endregion

    //#region Remote Platforms

    // Data Grid
    @ViewChild('remotePlatformLogsGrid')
    public remotePlatformLogsGrid: DxDataGridComponent;
    @ViewChild('remotePlatformLogPopup')
    remotePlatformLogPopup: RemotePlatformLogComponent;
    @ViewChild('remotePlatformPicker')
    public remotePlatformPicker: RemotePlatformPickerComponent;
    // File transfer logs
    public remotePlatforms: IOcbRemotePlatform[] = null;
    public remotePlatformLogs: IOcbRemotePlatformLog[] = null;
    private requestedPlatformId?: string;
    public selectedRemotePlatform: IOcbRemotePlatform = null;
    // Popup Display
    //public isRemotePlatformLogDisplayPopupVisible: boolean = false;
    public displayedRemotePlatformLog: IOcbRemotePlatformLog = null;
    // Logs types labels in JSON object
    private remotePlatformsLogsTypesLabels: any[];


    public displayRemotePlatformLogDate(rowData): Date {
        return this.displayDate(rowData.logDate);
    }

    public displayRemotePlatformLogDateAsString(rowData): string {
        const locale = this.getCurrentLocale();
        return this.displayDate(rowData.logDate).toLocaleString(locale);
    }

    public displayRemotePlatformLogType(rowData) {
        return this.remotePlatformsLogsTypesLabels[OcbRemotePlatformLogTypes[rowData.logType]];
    }


    private loadRemotePlatforms(): void {
        this.currentEnvironmentId = this.route.snapshot.params.id;
        this.remotePlatformsService.getRemotePlatforms(this.currentEnvironmentId)
            .pipe(
                finalize(() => this.isLoadPanelVisible = false))
            .subscribe({
                next: (res) => {
                    this.translateService.get('_commons.alle').subscribe((msg) => {
                        const allPlatforms = { id: 'all', displayName: msg } as IOcbRemotePlatform;
                        res.unshift(allPlatforms);
                        this.remotePlatforms = res;
                        if (this.requestedPlatformId) {
                            this.tabs.selectedIndex = 1;
                            this.remotePlatformPicker.selectRemotePlatform(this.requestedPlatformId);
                        }
                        
                    });
                },
                error: (err) => this.handleError(err)
            });
    }

    private loadRemotePlatformLogs(): void {
        if (this.selectedRemotePlatform) {
            this.showRemotePlatformLoading$('_commons.loading')
                .pipe(
                    mergeMap(() => {
                        if (this.selectedRemotePlatform.id != 'all') {
                            this.showRemotePlatformColumn = false;
                            return this.remotePlatformsService.getLogs(this.selectedRemotePlatform.id, this.getSelectedStartDate(), this.getSelectedEndDate());
                        }
                        else {
                            this.showRemotePlatformColumn = true;
                            return this.remotePlatformsService.getAllLogs(this.getSelectedStartDate(), this.getSelectedEndDate(), this.currentEnvironmentId);
                        }
                    }),
                    finalize(() => this.remotePlatformLogsGrid.instance.endCustomLoading()))
                .subscribe({
                    next: (res) => this.remotePlatformLogs = res,
                    error: (err) => this.handleError(err)
                });
        }
    }


    public onRemotePlatformRowDblClick(e): void {
        if (e?.data) {
            this.showRemotePlatformlogDetail(e, e.data);
        }
    }

    public remotePlatformChanged(data): void {
        if (data) {
            this.selectedRemotePlatform = data;
            this.loadRemotePlatformLogs();
        } else {
            this.remotePlatformLogs = [];
        }
    }

    private showRemotePlatformLoading$(messageKey: string): Observable<void> {
        return this.translateService.get(messageKey).pipe(
            tap(message => this.remotePlatformLogsGrid.instance.beginCustomLoading(`${message}...`)),
        );
    }

    public showRemotePlatformlogDetail(e, data) {
        this.displayedRemotePlatformLog = data;
        this.remotePlatformLogPopup.visible = true;
    }

    //#endregion

    //#region Environment logs

    // Data Grid
    @ViewChild('environmentLogsGrid')
    public environmentLogsGrid: DxDataGridComponent;
    @ViewChild('environmentLogPopup')
    environmentLogPopup: EnvironmentLogComponent;
    // Environment logs
    public environmentLogs: IOcbEnvironmentLog[] = null;
    // Popup Display
    public displayedEnvironmentLog: IOcbEnvironmentLog = null;
    // Logs types labels in JSON object
    private environmentLogsTypesLabels: any[];


    /**
     * Display value of date
     * @param rowData Current row data
     */
    public displayEnvironmentLogDate(rowData): Date {
        return this.displayDate(rowData.logDate);
    }

    /**
     * Display value of date
     * @param rowData Current row data
     */
    public displayEnvironmentLogDateAsString(rowData): string {
        const locale = this.getCurrentLocale();
        return this.displayDate(rowData.logDate).toLocaleString(locale);
    }

    /**
     * Display value of log type
     * @param rowData Current row data
     */
    public displayEnvironmentLogType(rowData) {
        return this.environmentLogsTypesLabels[OcbEnvironmentLogTypes[rowData.logType]];
    }


    /**
     * Load environments logs
     */
    private loadEnvironmentLogs() {
        this.showEnvironmentLogsLoading$('_commons.loading')
            .pipe(
                mergeMap(_ => this.environmentsService.getLogs(this.currentEnvironmentId, this.getSelectedStartDate(), this.getSelectedEndDate())),
                finalize(() => this.environmentLogsGrid.instance.endCustomLoading()))
            .subscribe(
                res => this.environmentLogs = res,
                err => this.handleError(err));
    }

    /**
     * Show command details
     * @param e Cell object
     */
    public onEnvironmentLogsRowDblClick(e): void {
        if (e && e.data) {
            this.showEnvironmentLogDetail(e, e.data);
        }
    }


    /**
     * Open popup with full environment log
     * @param e Handle event
     * @param data Log data to display
     */
    public showEnvironmentLogDetail(e, data) {
        this.displayedEnvironmentLog = data;
        this.environmentLogPopup.visible = true;
    }


    /**
     * Displays a loader over the data grid, with a localized message.
     * @param messageKey Localized message key.
     */
    private showEnvironmentLogsLoading$(messageKey: string): Observable<void> {
        return this.translateService.get(messageKey).pipe(
            tap(message => this.environmentLogsGrid.instance.beginCustomLoading(`${message}...`)),
        );
    }


    //#endregion

}
